import { useReducer, useEffect, useCallback, useMemo, useRef } from 'react';
import localforage from 'localforage';
import {
    GridActionsCellItem,
    GridColDef,
    GridPaginationModel,
    GridRenderCellParams,
    GridSortModel,
} from '@mui/x-data-grid';
import { Create, Delete, Edit, HolidayVillage } from '@mui/icons-material';
import { AdminClusterPageState, AdminClusterPageAction, BaseCluster, SortModelType } from '@/types/pages/admin/cluster';
import { createClusterAPI, deleteClusterAPI, editClusterAPI, fetchClusterListAPI } from '@/api/admin/rbac';
import BasePaper from '@/components/ui/BasePaper';
import BaseTitle from '@/components/ui/BaseTitle';
import BaseCard from '@/components/ui/BaseCard';
import SearchBar from '@/components/form/SearchBar';
import BaseDataTable from '@/components/ui/BaseDataTable';
import AdminClusterPageDialog from './AdminClusterPageDialog';
import { useAppContext } from '@/provider/contexts';
import BaseButton from '@/components/ui/BaseButton';
import { CreateClusterRequest, EditClusterRequest } from '@/types/pages/admin/apiRBAC';
import DeleteClusterDialog from './DeleteClusterDialog';
import { useTranslation } from 'react-i18next';
import BaseTableColumn from '@/components/ui/BaseTableColumn';

function reducer(state: AdminClusterPageState, action: AdminClusterPageAction) {
    switch (action.type) {
        case 'SET_TABLE_LOADING':
            return { ...state, tableLoading: action.payload };
        case 'SET_SEARCH_QUERY':
            return {
                ...state,
                searchQuery: action.payload,
            };
        case 'SET_CLUSTER_LIST':
            return {
                ...state,
                tableLoading: false,
                clusterList: action.payload.aaData,
                rowCount: action.payload.total_records,
            };
        case 'SET_IS_CREATE_DIALOG_OPEN':
            return { ...state, isCreateDialogOpen: action.payload };
        case 'SET_IS_EDIT_DIALOG_OPEN':
            return { ...state, isEditDialogOpen: action.payload };
        case 'SET_IS_DELETE_DIALOG_OPEN':
            return { ...state, isDeleteDialogOpen: action.payload };
        case 'SET_DELETE_CLUSTER':
            return { ...state, deleteClusterId: action.payload.id, deleteClusterName: action.payload.cluster_name };
        case 'SET_CAPTCHA_KEY':
            return {
                ...state,
                captchaKey: action.payload,
            };
        case 'SET_CAPTCHA':
            return {
                ...state,
                captcha: action.payload,
            };
        case 'SET_IS_CAPTCHA_ERROR':
            return {
                ...state,
                isCaptchaError: action.payload,
            };
        case 'SET_PAGINATION_MODEL':
            return { ...state, paginationModel: action.payload };
        case 'SET_EDIT_CLUSTER_ID':
            return { ...state, editClusterId: action.payload };
        case 'CLEAR_CAPTCHA':
            return {
                ...state,
                captchaKey: '',
                captcha: '',
                isCaptchaError: false,
            };
        case 'SET_SORT_MODEL':
            return {
                ...state,
                sortModel: action.payload,
            };
        default:
            return state;
    }
}

export default function AdminClusterPage() {
    const { t } = useTranslation('common');
    const { setNotification } = useAppContext();
    const tRef = useRef(t);
    const setNotificationRef = useRef(setNotification);

    const defaultState = {
        tableLoading: false,
        clusterList: [],
        searchQuery: '',
        rowCount: 0,
        isCreateDialogOpen: false,
        isEditDialogOpen: false,
        isDeleteDialogOpen: false,
        deleteClusterId: null,
        deleteClusterName: '',
        captchaKey: '',
        captcha: '',
        isCaptchaError: false,
        paginationModel: {
            page: 0,
            pageSize: 5,
        },
        editClusterId: null,
        sortModel: {} as SortModelType,
    };

    const [
        {
            tableLoading,
            clusterList,
            rowCount,
            searchQuery,
            isCreateDialogOpen,
            isEditDialogOpen,
            isDeleteDialogOpen,
            deleteClusterId,
            deleteClusterName,
            editClusterId,
            captchaKey,
            captcha,
            isCaptchaError,
            paginationModel,
            sortModel,
        },
        dispatch,
    ] = useReducer(reducer, defaultState);
    const fetchAbortControllerRef = useRef<AbortController | null>(null);
    const latestFetchIdRef = useRef(0);

    useEffect(() => {
        tRef.current = t;
    }, [t]);

    useEffect(() => {
        setNotificationRef.current = setNotification;
    }, [setNotification]);

    const editClusterHandler = useCallback(async (rowData: BaseCluster) => {
        dispatch({ type: 'SET_IS_EDIT_DIALOG_OPEN', payload: true });
        dispatch({
            type: 'SET_EDIT_CLUSTER_ID',
            payload: rowData.id,
        });
    }, []);

    const openDeleteClusterDialog = useCallback(
        async (rowData: BaseCluster) => {
            dispatch({
                type: 'SET_DELETE_CLUSTER',
                payload: rowData,
            });

            try {
                const corporationId = await localforage.getItem('corporation_id');
                const {
                    data: { data, message, isSuccess },
                } = await deleteClusterAPI(rowData.id, { cid: corporationId as number });

                if (isSuccess) {
                    dispatch({ type: 'SET_IS_DELETE_DIALOG_OPEN', payload: true });
                    dispatch({ type: 'SET_CAPTCHA_KEY', payload: data.captcha });
                } else {
                    setNotification({ message: message, severity: 'error' });
                }
            } catch (error) {
                setNotification({ message: error instanceof Error ? error.message : String(error), severity: 'error' });
            }
        },
        [setNotification],
    );

    const renderSimpleCell = useCallback((params: GridRenderCellParams) => {
        const value = params.value;
        const displayValue = value === null || value === undefined || value === '' ? '-' : String(value);
        return <BaseTableColumn columnValue={displayValue} />;
    }, []);

    const renderIndex = useCallback(
        (params: GridRenderCellParams) => {
            if (tableLoading) return <BaseTableColumn columnValue="" />;
            const index =
                params.api.getAllRowIds().indexOf(params.id) + 1 + paginationModel.page * paginationModel.pageSize;

            return <BaseTableColumn columnValue={String(index)} />;
        },
        [paginationModel.page, paginationModel.pageSize, tableLoading],
    );

    const clusterColumns: GridColDef[] = useMemo(
        () => [
            {
                field: 'index',
                headerName: '#',
                flex: 0.3,
                renderCell: renderIndex,
                sortable: false,
            },
            {
                field: 'cluster_name',
                headerName: t('Cluster Name'),
                flex: 1,
                renderCell: renderSimpleCell,
            },
            {
                field: 'stores_count',
                headerName: t('Store Num'),
                flex: 1,
                renderCell: renderSimpleCell,
            },
            {
                field: 'users_count',
                headerName: t('Account Num'),
                flex: 1,
                renderCell: renderSimpleCell,
            },
            {
                field: 'action',
                type: 'actions',
                headerName: t('Action'),
                flex: 1,
                minWidth: 140,
                getActions: ({ row }) => {
                    return [
                        <GridActionsCellItem
                            icon={<Edit />}
                            label={t('Edit')}
                            sx={{
                                color: 'primary.main',
                            }}
                            onClick={() => editClusterHandler(row)}
                        />,
                        <GridActionsCellItem
                            icon={<Delete />}
                            label={t('Delete')}
                            color="inherit"
                            onClick={() => openDeleteClusterDialog(row)}
                        />,
                    ];
                },
            },
        ],
        [editClusterHandler, openDeleteClusterDialog, renderIndex, renderSimpleCell, t],
    );
    const fetchClusterListHandler = useCallback(async () => {
        fetchAbortControllerRef.current?.abort();
        const fetchId = latestFetchIdRef.current + 1;
        latestFetchIdRef.current = fetchId;
        const abortController = new AbortController();
        fetchAbortControllerRef.current = abortController;
        dispatch({ type: 'SET_TABLE_LOADING', payload: true });
        const corporationId = await localforage.getItem('corporation_id');
        const { field, sort } = sortModel;
        const params = {
            cid: corporationId as number,
            f_cluster_name: searchQuery,
            current_page: paginationModel.page + 1,
            data_per_page: paginationModel.pageSize,
            sort_field: field,
            sort_order: sort,
        };
        try {
            const {
                data: { data, message, isSuccess },
            } = await fetchClusterListAPI(params, { signal: abortController.signal });

            if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;

            if (isSuccess) {
                dispatch({ type: 'SET_CLUSTER_LIST', payload: data });
            } else {
                dispatch({ type: 'SET_TABLE_LOADING', payload: false });
                setNotificationRef.current({
                    severity: 'error',
                    message: message,
                });
            }
        } catch (error) {
            if (abortController.signal.aborted || latestFetchIdRef.current !== fetchId) return;
            dispatch({ type: 'SET_TABLE_LOADING', payload: false });
            setNotificationRef.current({
                severity: 'error',
                message: error instanceof Error ? error.message : tRef.current('An unexpected error occurred'),
            });
        } finally {
            if (latestFetchIdRef.current === fetchId) {
                fetchAbortControllerRef.current = null;
            }
        }
    }, [paginationModel.page, paginationModel.pageSize, searchQuery, sortModel]);

    const searchHandler = async (value: string) => {
        dispatch({ type: 'SET_SEARCH_QUERY', payload: value });
        dispatch({ type: 'SET_PAGINATION_MODEL', payload: { ...paginationModel, page: 0 } });
    };

    const pageChangeHandler = (pageModel: GridPaginationModel) => {
        if (paginationModel.pageSize !== pageModel.pageSize) {
            // When the table page size changes, reset the page to 1.
            dispatch({
                type: 'SET_PAGINATION_MODEL',
                payload: { ...pageModel, page: 0 },
            });
        } else {
            dispatch({ type: 'SET_PAGINATION_MODEL', payload: pageModel });
        }
    };

    const sortModelChangeHandler = (sortModel: GridSortModel) => {
        if (sortModel.length > 0) {
            const { field, sort } = sortModel[0];
            dispatch({
                type: 'SET_SORT_MODEL',
                payload: { field, sort: sort ?? null },
            });
        }
    };

    const createClusterHandler = async (createClusterData: CreateClusterRequest) => {
        dispatch({ type: 'SET_IS_CREATE_DIALOG_OPEN', payload: false });

        try {
            const corporationId = await localforage.getItem('corporation_id');
            const passData = {
                ...createClusterData,
                cid: corporationId as number,
            };
            const {
                data: { status, isSuccess, message },
            } = await createClusterAPI(passData);

            if (isSuccess) {
                setNotification({
                    severity: 'success',
                    message,
                });
                await fetchClusterListHandler();
            } else {
                setNotification({
                    severity: 'error',
                    message: status === 400 ? message : tRef.current('Failed to create cluster.'),
                });
            }
        } catch (error) {
            setNotification({
                severity: 'error',
                message: error instanceof Error ? error.message : String(error),
            });
        }
    };

    const confirmDeleteClusterHandler = async () => {
        if (captchaKey !== captcha) {
            dispatch({ type: 'SET_IS_CAPTCHA_ERROR', payload: true });
            return;
        }
        try {
            const corporationId = await localforage.getItem('corporation_id');
            const deleteData = { captcha: captchaKey, cid: corporationId as number };

            const {
                data: { message, isSuccess },
            } = await deleteClusterAPI(deleteClusterId as number, deleteData);

            if (isSuccess) {
                closeDeleteClusterDialog();
                setNotification({
                    severity: 'success',
                    message: message,
                });
                fetchClusterListHandler();
            } else {
                setNotification({
                    severity: 'error',
                    message: message,
                });
            }
        } catch (error) {
            setNotification({
                severity: 'error',
                message: error instanceof Error ? error.message : String(error),
            });
        }
        closeDeleteClusterDialog();
    };

    const closeDeleteClusterDialog = () => {
        dispatch({ type: 'SET_IS_DELETE_DIALOG_OPEN', payload: false });
        dispatch({ type: 'CLEAR_CAPTCHA' });
    };

    const confirmEditCluster = async (editData: EditClusterRequest, editClusterId: number) => {
        dispatch({ type: 'SET_IS_EDIT_DIALOG_OPEN', payload: false });
        try {
            const {
                data: { message, isSuccess },
            } = await editClusterAPI(editClusterId, editData);
            if (isSuccess) {
                setNotification({
                    severity: 'success',
                    message,
                });
                await fetchClusterListHandler();
            } else {
                setNotification({
                    severity: 'error',
                    message,
                });
            }
        } catch (error) {
            setNotification({
                severity: 'error',
                message: error instanceof Error ? error.message : String(error),
            });
        }
    };

    useEffect(() => {
        fetchClusterListHandler();
    }, [fetchClusterListHandler]);

    useEffect(() => {
        return () => {
            fetchAbortControllerRef.current?.abort();
        };
    }, []);

    return (
        <>
            <BasePaper className="admin-paper">
                <BaseTitle icon={<HolidayVillage />} title={t('Cluster')} />

                <BaseCard>
                    <div className="card-header">
                        <SearchBar onSearch={searchHandler} sx={{ my: 2 }} />
                        <BaseButton
                            startIcon={<Create />}
                            onClick={() => {
                                dispatch({
                                    type: 'SET_IS_CREATE_DIALOG_OPEN',
                                    payload: true,
                                });
                            }}
                        >
                            {t('New Cluster')}
                        </BaseButton>
                    </div>
                    <BaseDataTable
                        rows={clusterList}
                        columns={clusterColumns}
                        loading={tableLoading}
                        paginationMode="server"
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={pageChangeHandler}
                        onSortModelChange={sortModelChangeHandler}
                        sortingMode="server"
                    />
                </BaseCard>
            </BasePaper>
            <AdminClusterPageDialog
                dialogType="Create"
                isDialogOpen={isCreateDialogOpen}
                onConfirm={createClusterHandler}
                onClose={() =>
                    dispatch({
                        type: 'SET_IS_CREATE_DIALOG_OPEN',
                        payload: false,
                    })
                }
            />
            <AdminClusterPageDialog
                dialogType="Edit"
                isDialogOpen={isEditDialogOpen}
                editClusterId={editClusterId}
                onConfirm={(data, id) => confirmEditCluster(data as EditClusterRequest, id as number)}
                onClose={() =>
                    dispatch({
                        type: 'SET_IS_EDIT_DIALOG_OPEN',
                        payload: false,
                    })
                }
            />
            <DeleteClusterDialog
                clusterName={deleteClusterName}
                isDialogOpen={isDeleteDialogOpen}
                onConfirmDelete={confirmDeleteClusterHandler}
                onClose={closeDeleteClusterDialog}
                captchaKey={captchaKey}
                isCaptchaError={isCaptchaError}
                setCaptcha={(captcha) => dispatch({ type: 'SET_CAPTCHA', payload: captcha })}
            />
        </>
    );
}
